import { useCallback, useEffect, useRef, useState } from "react";
import ContainerSimple from "../../../Components/ContainerSimple";
import Custom from "../../../Components/Buttons/Custom";
import MultipleSelectAdmin from "../../../Components/InputFields/MultipleSelectAdmin";
import { API_BASE_URL } from "../../../config/api";

const USERS_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/users/list/`;
const PHASES_ENDPOINT =
  `${API_BASE_URL}/api/v0/challenges/admin/phases/`;
const CREATE_ACCOUNT_ENDPOINT =
  `${API_BASE_URL}/api/v0/challenges/admin/accounts/create/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function buildUsersUrl({ page, searchTerm }) {
  const params = new URLSearchParams();

  params.set("page", String(page));

  if (searchTerm) {
    params.set("search", searchTerm);
  }

  return `${USERS_ENDPOINT}?${params.toString()}`;
}

function mergeUsers(previousUsers, nextUsers) {
  const seen = new Set(previousUsers.map((user) => user.id));
  const uniqueUsers = nextUsers.filter((user) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });

  return [...previousUsers, ...uniqueUsers];
}

function formatUserDisplay(user) {
  if (!user) return null;

  if (user.fn && user.ln) {
    return `${user.fn} ${user.ln} (${user.code})`;
  }

  if (user.email) {
    return `${user.email} (${user.code})`;
  }

  return `- (${user.code ?? "-"})`;
}

export default function CreateAccountModal({ onClose }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [displayUser, setDisplayUser] = useState("");
  const [displayPhase, setDisplayPhase] = useState("");

  const [userData, setUserData] = useState([]);
  const [userSearchState, setUserSearchState] = useState("");
  const [appliedUserSearch, setAppliedUserSearch] = useState("");
  const [userPagination, setUserPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isUserLoadingMore, setIsUserLoadingMore] = useState(false);

  const [phaseData, setPhaseData] = useState([]);

  const userRequestIdRef = useRef(0);

  useEffect(() => {
    if (!selectedUser) {
      setDisplayUser(null);
      return;
    }

    const user = userData.find((item) => item.id === selectedUser);

    if (user) {
      setDisplayUser(formatUserDisplay(user));
    }
  }, [selectedUser, userData]);

  useEffect(() => {
    if (!selectedPhase) {
      setDisplayPhase(null);
      return;
    }

    const phase = phaseData.find((item) => item.phase_id === selectedPhase);

    if (!phase) {
      return;
    }

    let text = phase.challenge_type === "real" ? "واقعی - " : "تمرینی - ";
    text += `${phase.challenge_category} - ${phase.challenge_name} - `;
    text +=
      phase.phase_type === "phase1"
        ? "فاز ۱"
        : phase.phase_type === "phase2"
          ? "فاز ۲"
          : "فاندد";

    setDisplayPhase(text);
  }, [selectedPhase, phaseData]);

  const fetchUserData = useCallback(
    async ({ page = 1, replace = false, searchTerm }) => {
      const requestId = userRequestIdRef.current + 1;
      userRequestIdRef.current = requestId;

      if (replace) {
        setIsUserLoading(true);
        setIsUserLoadingMore(false);
      } else {
        setIsUserLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildUsersUrl({
            page,
            searchTerm,
          }),
        );

        if (!data || requestId !== userRequestIdRef.current) {
          return;
        }

        const nextUsers = Array.isArray(data.results) ? data.results : [];

        setUserData((previousUsers) =>
          replace ? nextUsers : mergeUsers(previousUsers, nextUsers),
        );
        setUserPagination({
          page: data.page ?? page,
          count: data.count ?? nextUsers.length,
          pageSize: data.page_size ?? nextUsers.length,
          totalPages: data.total_pages ?? page,
        });
      } catch (error) {
        if (requestId !== userRequestIdRef.current) {
          return;
        }

        console.error("Error fetching user data:", error);

        if (replace) {
          setUserData([]);
        }
      } finally {
        if (requestId === userRequestIdRef.current) {
          setIsUserLoading(false);
          setIsUserLoadingMore(false);
        }
      }
    },
    [],
  );

  const fetchPhaseData = useCallback(async () => {
    try {
      const data = await authorizedGet(PHASES_ENDPOINT);
      if (!data) {
        return;
      }

      setPhaseData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching phase data:", error);
    }
  }, []);

  useEffect(() => {
    fetchUserData({
      page: 1,
      replace: true,
      searchTerm: appliedUserSearch,
    });
  }, [appliedUserSearch, fetchUserData]);

  useEffect(() => {
    fetchPhaseData();
  }, [fetchPhaseData]);

  const handleUserSearchClick = () => {
    const nextSearch = userSearchState.trim();

    if (nextSearch === appliedUserSearch) {
      fetchUserData({
        page: 1,
        replace: true,
        searchTerm: nextSearch,
      });
      return;
    }

    setAppliedUserSearch(nextSearch);
  };

  const handleUserTableScroll = (event) => {
    const element = event.currentTarget;

    if (
      isUserLoading ||
      isUserLoadingMore ||
      userPagination.page >= userPagination.totalPages
    ) {
      return;
    }

    if (element.scrollTop + element.clientHeight < element.scrollHeight - 80) {
      return;
    }

    fetchUserData({
      page: userPagination.page + 1,
      replace: false,
      searchTerm: appliedUserSearch,
    });
  };

  const onUserClick = (userId) => {
    setSelectedUser(userId);

    if (!userId) {
      setDisplayUser(null);
    }
  };

  const onPhaseClick = (phaseId) => {
    setSelectedPhase(phaseId);
  };

  const createAccount = async () => {
    try {
      let response = await fetch(CREATE_ACCOUNT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          user_id: selectedUser,
          phase_id: selectedPhase,
        }),
      });

      if (response.status === 401) {
        const refreshResponse = await fetch(REFRESH_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: localStorage.getItem("refresh") }),
        });

        if (!refreshResponse.ok) {
          logoutAndRedirect();
          return { ok: false, error: "unauthorized" };
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(CREATE_ACCOUNT_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            user_id: selectedUser,
            phase_id: selectedPhase,
          }),
        });
      }

      let data = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        return {
          ok: false,
          data,
          error: data?.detail || `HTTP ${response.status}`,
        };
      }

      return { ok: true, data };
    } catch (error) {
      return { ok: false, error: error?.message || "network_error" };
    }
  };

  return (
    <ContainerSimple>
      <MultipleSelectAdmin
        header="انتخاب چالش"
        search={false}
        footer="چالش انتخاب شده"
        footerValue={displayPhase}
        onRowClick={onPhaseClick}
        tableColumns={[
          { key: "code", label: "کد" },
          { key: "type", label: "نوع چالش" },
          { key: "category", label: "دسته‌بندی چالش" },
          { key: "name", label: "نام چالش" },
          { key: "phase", label: "فاز" },
        ]}
        tableData={phaseData.map((phase) => ({
          id: phase.phase_id,
          code: phase.phase_id,
          type: phase.challenge_type === "real" ? "واقعی" : "تمرینی",
          category: phase.challenge_category,
          name: phase.challenge_name,
          phase:
            phase.phase_type === "phase1"
              ? "فاز ۱"
              : phase.phase_type === "phase2"
                ? "فاز ۲"
                : "فاندد",
        }))}
      />
      <MultipleSelectAdmin
        header="انتخاب کاربر"
        search={true}
        searchValue={userSearchState}
        setSearchState={setUserSearchState}
        searchClick={handleUserSearchClick}
        footer="کاربر انتخاب شده"
        footerValue={displayUser}
        onRowClick={onUserClick}
        onTableScroll={handleUserTableScroll}
        isLoadingMore={isUserLoading || isUserLoadingMore}
        tableColumns={[
          { key: "code", label: "کد" },
          { key: "name", label: "نام" },
        ]}
        tableData={userData.map((user) => ({
          id: user.id,
          code: user.code,
          name:
            user.fn && user.ln ? `${user.fn} ${user.ln}` : (user.email ?? "-"),
        }))}
      />
      <Custom
        text="ایجاد حساب چالش جدید"
        svg_name="add"
        svgClassName="stroke-zi-text-dark"
        href=""
        className="!rounded-lg !p-2.5 text-zi-text-dark mt-6 bg-zi-gradient-button-blue"
        textShadow={true}
        svgShadow={true}
        onClick={async () => {
          const result = await createAccount();
          if (result.ok) {
            onClose();
            window.location.reload();
          }
        }}
      />
    </ContainerSimple>
  );
}
